import React, { useEffect, useState, ChangeEvent, useContext } from "react";
import { useForm, SubmitHandler, UseFormRegister } from "react-hook-form";
import { useLocation, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import CredentialsForm from "@/components/Form/Form";
import CredentialsInput from "@/components/ui/Input";
import CredentialsFooter from "@/components/footer/Footer";
import CredentialsHeader from "@/components/header/Header";
import NeedHelpButton from "@/components/need-help-button/need-help-button";
import CardNumberHint, { CARD_NUMBER_HINT_ID } from "@/components/card-number-hint/CardNumberHint";

import { tryCatch } from "@/utils/try-catch";

import { useApiResponseHandler } from "@/hooks/useApiResponseHandler";

import { UserContext } from "@/contexts/continente-credentials/UserContext";
import { DigitalAssetContext } from "@/contexts/continente-credentials/DigitalAssetContext";

import { FormError } from "@/models/continente-credentials/common/FormError";
import { CardAssociation } from "@/models/continente-credentials/mobile/CardAssociation";

import { cardAssociationService } from "@/services/continente-credentials/mobile/CardAssociationService";
import { AuthFlowType, FLOW_CONFIGS, FlowConfig, FlowType } from "@/config/LoyaltyCardAssociationFlowConfig";
import { Button } from "@/components/ui/button";
import ButtonContainer from "@/components/button-container";

interface AssociateIdentifierPageProps {
  flowConfig: FlowConfig;
}

type Form = CardAssociation & FormError;

export default function SharedLoyaltyCardAssociation({ flowConfig }: AssociateIdentifierPageProps) {
  const history = useHistory();
  const { t } = useTranslation();
  const userContext = useContext(UserContext);
  const digitalAssetContext = useContext(DigitalAssetContext);

  const [loading, setLoading] = useState(false);
  const [cardValue, setCardValue] = useState("");
  const location = useLocation<any>();
  const [labelCardVisible, setLabelCardVisible] = useState<boolean>(false);

  const ANCHOR_NEED_HELP = "faq8";
  const { handleResponse } = useApiResponseHandler();

  const nextStep = location?.state?.nextStep || "";
  const isDeepLink = location.state?.from == "dl";

  const formState = useForm<Form>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = formState;

  const skipAssociation = async () => {
    setLoading(true);
    if (
      location.state != null &&
      !nextStep.length &&
      !location.state.properties.email.length
    ) {
      throw new Error(
        "Location State does not have all the necessary fields."
      );
    }

    const { data: response, error } = await tryCatch(cardAssociationService.cardAssociation(
      {
        cardNumber: "0",
        addCC: false,
        skipAssociation: true,
      },
      nextStep
    ));

    handleResponse(response, {
      setError,
    }, error);
    setLoading(false);
  };

  const SignUpClick = async () => {
    const { data: response, error } = await tryCatch(
      cardAssociationService.cardAssociation(
        {
          cardNumber: "0",
          addCC: true,
          skipAssociation: false,
        },
        nextStep
      ));

    handleResponse(response, {
      setError,
      onSuccess: (response) => {
        userContext.updateUser({
          ...userContext.user,
          phoneNumber:
            userContext.user.phoneNumber === ""
              ? response.value.properties.phoneNumber
              : userContext.user.phoneNumber,
        });

        history.push(response.value.nextStep.substring(4), response.value);
      }
    }, error);

    setLoading(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (
      e.key !== "Enter" &&
      (!e.key.match(/[0-9\s]/) ||
        (e.target as HTMLInputElement).value.length > 12)
    ) {
      e.preventDefault();
    }
  };

  const handleCardChange = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>) => {
    clearErrors();

    let cardNumber = value.replace(/\s/g, "");

    setValue("cardNumber", cardNumber);
    setLabelCardVisible(value.length > 0 ? true : false);
    setCardValue(cardNumber);
  };

  const onSubmit: SubmitHandler<CardAssociation> = async (data) => {
    setLoading(true);
    const { data: response, error } = await tryCatch(cardAssociationService.cardAssociation(
      {
        cardNumber: data.cardNumber,
        addCC: false,
        skipAssociation: false,
      },
      nextStep
    ));

    handleResponse(response, {
      setError
    }, error);

    setLoading(false);
  };

  useEffect(() => {
    const CardValidator = (register: UseFormRegister<Form>) =>
      register("cardNumber", {
        required: {
          value: true,
          message: t("common.required_field"),
        },
        pattern: {
          value: /^[0-9]{13}$/,
          message: t("card_association.invalid_loyaltycard"),
        },
      });
    CardValidator(register);
  }, [register, t]);

  return (
    <CredentialsForm
      id={flowConfig.form}
      form={formState}
      autoComplete="off"
      onSubmit={(e) => {
        clearErrors();
        handleSubmit(onSubmit)(e);
      }}
    >
      <CredentialsHeader
        title={t(flowConfig.labels.headerTitle)}
        subtitle={t(flowConfig.labels.headerSubtitle || "")}
        showLoading={loading}
        skipStep={flowConfig.type === FlowType.ASSOCIATION && (!digitalAssetContext.digitalAsset.requiredCardAssociation && flowConfig.skipStep) ? skipAssociation : undefined}
        previousStepBackXTimes={flowConfig.backSteps}
        showBackButton={!isDeepLink}
      />
      <CredentialsInput
        id="card_input"
        name="card_input"
        type={"text"}
        value={cardValue}
        onChange={handleCardChange}
        required={true}
        labelText={t(flowConfig.labels.input)}
        isFilled={labelCardVisible}
        isError={!!(errors.cardNumber || errors.blocked || errors.submitError)}
        errorMsg={
          errors.cardNumber?.message ||
          errors.blocked?.message ||
          errors.submitError?.message ||
          ""
        }
        onKeyPress={handleKeyPress}
        errorMsgBlocked={errors.blocked ? errors.blocked.message : ""}
        onInvalid={(e) => {
          e.preventDefault()
          if (e.target.validity.valueMissing) {
            setError("cardNumber", { message: t("common.required_field") });
          }
        }}
        disabled={loading}
        ariaLabel={t(flowConfig.labels.inputAria.required)}
        ariaDescribedBy={CARD_NUMBER_HINT_ID}
        placeholder={flowConfig.labels.placeholder ? t(flowConfig.labels.placeholder) : undefined}
      />
      <CardNumberHint />
      <ButtonContainer>
        <Button
          disabled={loading}
          aria-label={!loading ? t(flowConfig.labels.nextButtonAria.enabled) : t(flowConfig.labels.nextButtonAria.disabled)}
          type="submit"
        >
          {t(flowConfig.labels.nextButton)}
        </Button>
        {flowConfig.canCreateCard && (
          <Button
            disabled={loading}
            onClick={loading ? () => { } : SignUpClick}
            aria-label={t("card_association.card_not_exist_message2")}
            type="button"
            variant="secondary"
          >
            {t("card_association.card_not_exist_message2")}
          </Button>
        )}
        <NeedHelpButton anchor={ANCHOR_NEED_HELP} />
      </ButtonContainer>
      <div
        role="note"
        aria-label={t("accessibility.signin.icons")}
        className="flex flex-col">
        <CredentialsFooter />
      </div>
    </CredentialsForm>
  );
}

export const createAssociateIdentifierPage = (flowConfig: AuthFlowType) => {
  const config = FLOW_CONFIGS[flowConfig];
  return () => <SharedLoyaltyCardAssociation flowConfig={config} />;
};

export const LoyaltyCardAssociation = createAssociateIdentifierPage(AuthFlowType.CREDENTIALS);
export const SCLoyaltyCardAssociation = createAssociateIdentifierPage(AuthFlowType.SITE_CREDENTIALS);
export const SCPasswordRecoverLoyaltyCardAssociation = createAssociateIdentifierPage(AuthFlowType.SITE_CREDENTIALS_PW_RECOVER);
